import { computed } from 'mobx'
import { task } from 'mobx-task'
import { dump } from 'js-yaml'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  FetchInventoryItemExportsFn,
  ImportInventoryItemsFn,
} from '@/features/inventory'
import type { InventoryItemExport } from '@/features/inventory/inventory-item-export'
import {
  getDocumentInventoryItemIds,
  type LegendDocument,
  replaceDocumentInventoryItem,
} from '@/features/legends/editor/legend-document'
import type { WorkspaceCatalog } from '@/features/legends/editor/legend-part-dependencies'
import { waitForPaint } from '@/features/legends/editor/wait-for-paint'
import type { SavedLegendVersion } from '@/features/legends/LegendStore'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import type { SelectLegendVersionOptions } from '@/features/legends/versioning/VersionManagerViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { Id } from '@/infra/id'
import { saveFile } from '@/utils/file-util'
import { createLegendExport, parseLegendExport } from './legend-export'

/**
 * Dependencies of the transfer: the inventory feature, alerts, and what it
 * does with the version being edited.
 */
export interface LegendTransferViewModelOptions {
  catalog: WorkspaceCatalog
  fetchInventoryItemExports: FetchInventoryItemExportsFn
  importInventoryItems: ImportInventoryItemsFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  getWorkspace: () => Workspace | null
  getLegendVersion: () => LegendVersion | null
  /**
   * Whether a part of the version failed to load.
   */
  hasHydrationError: () => boolean
  isEditingDisabled: () => boolean
  /**
   * The legend as the builders have it.
   */
  serialize: () => LegendDocument
  /**
   * Saves the whole document now.
   *
   * @returns `null` when nothing was saved.
   */
  saveNow: (document: LegendDocument) => Promise<SavedLegendVersion | null>
  selectLegendVersion: (
    version: number,
    options: SelectLegendVersionOptions,
  ) => Promise<unknown>
  /**
   * Fetches the workspace's service items again.
   */
  refreshInventoryItems: () => Promise<void>
}

/**
 * View model for exporting the selected version to a YAML file, and
 * importing one over the draft. Service items travel with the legend and are
 * imported into the workspace.
 */
export class LegendTransferViewModel {
  constructor(private readonly _options: LegendTransferViewModelOptions) {}

  /**
   * Whether an export or import is running.
   */
  @computed
  get isBusy() {
    return this.exportVersion.pending || this.importVersion.pending
  }

  /**
   * Whether exporting is unavailable: also while a part of the version failed
   * to load, since the export would leave it out.
   */
  @computed
  get isExportDisabled() {
    return this.isBusy || this._options.hasHydrationError()
  }

  /**
   * Whether importing, which replaces the draft, is unavailable.
   */
  @computed
  get isImportDisabled() {
    return this._options.isEditingDisabled() || this.isBusy
  }

  /**
   * Downloads the selected version, with its service items. A failure is
   * reported by the alert.
   */
  exportVersion = task.resolved(() =>
    this._exportVersion()
      .catch(this._options.createAlertErrorHandler('Export failed'))
      .catch(() => undefined),
  )

  /**
   * Replaces the draft with the legend in the file, importing its service
   * items into the workspace. A failure is reported by the alert of the step
   * that failed.
   */
  importVersion = task.resolved(async (files: FileList) => {
    const file = files.item(0)
    if (!file) {
      return
    }

    const alert = this._options
      .newAlert()
      .withState('pending')
      .saying(`Importing "${file.name}"`, 'Please wait…')
      .show()

    let parsed: ReturnType<typeof parseLegendExport>
    try {
      parsed = parseLegendExport(await file.text())
    } catch {
      alert
        .withState('error')
        .saying(
          'Import failed',
          'It seems this file is corrupted or not compatible with this version of Legend Builder.',
        )
      return
    }

    const isImported = await this._importDocument(parsed).catch(() => false)
    if (!isImported) {
      alert.dismiss()
      return
    }
    alert
      .withState('success')
      .saying('Imported', `Successfully imported "${file.name}"!`)
      .autoDismiss()
  })

  private async _exportVersion() {
    // SAFETY: only offered once a version is loaded.
    const version = this._options.getLegendVersion()!
    await waitForPaint()
    const document = this._options.serialize()
    const items = await this._fetchIncludedInventoryItems(
      getDocumentInventoryItemIds(document),
    )
    const exported = createLegendExport(
      document,
      version,
      this._options.catalog.skills,
      items,
    )

    const workspaceSlug = this._options.getWorkspace()?.slug ?? 'workspace'
    saveFile(
      new Blob([dump(exported, { noRefs: true, skipInvalid: true })], {
        type: 'application/yaml;charset=utf-8',
      }),
      `[${workspaceSlug}] ${version.name} (V${version.version}).yaml`,
    )
  }

  /**
   * Every step reports its own failure; saving and loading the service items
   * do so themselves.
   *
   * @param parsed
   * @returns Whether the draft was replaced: keeping someone else's changes
   * leaves it as it is.
   */
  private async _importDocument({
    document,
    inventoryItems,
  }: ReturnType<typeof parseLegendExport>) {
    const imported = await this._importIncludedInventoryItems(
      inventoryItems,
    ).catch(
      this._options.createAlertErrorHandler(
        'Importing the service items failed',
      ),
    )
    const withItems = imported.reduce(
      (current, item) =>
        replaceDocumentInventoryItem(
          current,
          item.previousItemId,
          item.newItemId,
        ),
      document,
    )

    // Roles from another workspace don't exist here.
    const roleIds = new Set(this._options.catalog.roles.map((r) => r.id))
    const saved = await this._options.saveNow({
      ...withItems,
      options: {
        ...withItems.options,
        targetRoles: (withItems.options.targetRoles ?? []).filter((roleId) =>
          roleIds.has(roleId),
        ),
      },
    })
    if (!saved) {
      return false
    }
    // SAFETY: only offered once a version is loaded.
    const current = this._options.getLegendVersion()!
    await this._options
      .selectLegendVersion(current.version, {
        // Lets Discard changes undo the import.
        keepInitialState: true,
      })
      .catch(
        this._options.createAlertErrorHandler(
          'Loading the imported legend failed',
        ),
      )
    await this._options.refreshInventoryItems()
    return true
  }

  private async _fetchIncludedInventoryItems(ids: string[]) {
    const workspaceId = this._options.getWorkspace()?.id
    if (!Id.some(workspaceId) || ids.length === 0) {
      return []
    }
    return await this._options.fetchInventoryItemExports(ids, workspaceId)
  }

  private async _importIncludedInventoryItems(items: InventoryItemExport[]) {
    const workspaceId = this._options.getWorkspace()?.id
    if (!Id.some(workspaceId) || items.length === 0) {
      return []
    }
    return await this._options.importInventoryItems(items, workspaceId)
  }
}
